<template>
  <div>
    <PageTitle title="Wash Packages" />
    <div class="fadein w-full flex flex-col mb-8 px-4 xl:px-8">

      <!-- SUMMARY BAR -->
      <div v-if="!loading && hasDataToDisplay" class="flex flex-wrap sm:flex-nowrap items-center text-left gap-2 mb-5 text-sm text-gray-500 dark:text-gray-400">
        <span class="font-medium text-gray-700 dark:text-gray-300">{{ washes.length }} packages</span>
        <span class="text-gray-500">·</span>
        <span>{{ totalContainerMappings }} container mappings</span>
        <span v-if="unconfiguredCount > 0" class="text-gray-300 hidden sm:inline-block">·</span>
        <span v-if="unconfiguredCount > 0" class="w-full sm:w-auto flex items-center gap-2 sm:gap-1 text-amber-500 font-medium">
          <font-awesome-icon icon="fa-solid fa-triangle-exclamation" class="h-3 w-3" />
          {{ unconfiguredCount }} unassigned
        </span>
      </div>

      <!-- LOADING -->
      <LoadingSkeleton v-if="loading" />

      <!-- CARD GRID -->
      <div
        v-else-if="hasDataToDisplay && !showLoadError"
        class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
      >
        <button
          v-for="wash in washes"
          :key="wash.wash_number"
          @click="openEditModalFunction(wash)"
          class="group bg-white dark:bg-primarydark-900/70 dark:hover:bg-primarydark-900  border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer overflow-hidden"
        >
          <!-- CARD HEADER -->
          <div class="flex items-center justify-between px-5 pt-4 pb-3">
            <div class="flex items-center gap-2 min-w-0">
              <!-- Unassigned warning dot -->
              <span
                v-if="!wash.containers || wash.containers.length === 0"
                class="flex-shrink-0 h-2 w-2 rounded-full bg-amber-400"
                title="No containers assigned"
              />
              <h3 class="font-semibold text-gray-800 dark:text-white/80 truncate text-sm leading-tight">
                {{ wash.wash_name }}
              </h3>
            </div>
            <div class="flex items-center gap-2 flex-shrink-0 ml-2">
              <span class="text-xs font-semibold text-primary-600 bg-primary-800/5 dark:text-primary-50 dark:bg-primary-200/5 dark:ring-1 dark:ring-primary-50/10 rounded-full px-2 py-0.5">
                #{{ wash.wash_number }}
              </span>
              <!-- <font-awesome-icon
                icon="fa-solid fa-pen"
                class="h-3 w-3 text-gray-300 group-hover:text-primary-400 transition-colors"
              /> -->
            </div>
          </div>

          <!-- DIVIDER -->
          <div class="border-t border-gray-100 dark:border-gray-600 mx-5" />

          <!-- CARD BODY — CONTAINERS -->
          <div class="px-5 py-3 min-h-[3.5rem]">
            <!-- Containers present -->
            <div v-if="wash.containers && wash.containers.length > 0" class="flex flex-wrap gap-1.5">
              <span
                v-for="container in visibleContainers(wash)"
                :key="container.id"
                class="inline-flex items-center rounded-md bg-primary-500 dark:bg-primarydark/30 dark:border dark:border-primarydark/40 px-2 py-0.5 text-xs font-medium text-white"
              >
                {{ container.name }}
              </span>
              <span
                v-if="wash.containers.length > maxVisibleChips"
                class="inline-flex items-center rounded-md bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-xs font-medium text-gray-500 dark:text-gray-300 border border-gray-200 dark:border-gray-500"
              >
                +{{ wash.containers.length - maxVisibleChips }} more
              </span>
            </div>
            <!-- No containers -->
            <div v-else class="flex items-center gap-2 h-full">
              <span class="text-xs text-gray-400 italic">No containers assigned</span>
            </div>
          </div>

          <!-- CARD FOOTER — container count -->
          <div class="px-5 pb-3 pt-1 flex items-center justify-end">
            <span
              :class="[
                'text-xs font-medium',
                wash.containers && wash.containers.length > 0 ? 'text-gray-400' : 'text-gray-300'
              ]"
            >
              {{ wash.containers ? wash.containers.length : 0 }}
              {{ wash.containers && wash.containers.length === 1 ? 'container' : 'containers' }}
            </span>
          </div>
        </button>
      </div>

      <!-- EMPTY STATE -->
      <div v-else-if="!showLoadError" class="py-16 lg:pt-24">
        <p>Wash Packages not configured. Please contact support.</p>
      </div>

      <!-- LOAD ERROR -->
      <div
        v-if="showLoadError && !loading"
        class="flex flex-col justify-center items-center w-full my-16"
      >
        <font-awesome-icon icon="fa-regular fa-circle-exclamation" class="m-1 h-6 w-6" />
        <h3 class="py-2">
          Unable to load Wash Packages
        </h3>
        <VButton
          @click="loadData"
          label="Try Again"
          uppercase
          class="my-5"
        />
      </div>

    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from "vuex"
import PageTitle from "@/components/PageTitle.vue"
import LoadingSkeleton from "@/components/Loading/LoadingSkeleton/LoadingSkeleton.vue"
import VButton from "@/components/Buttons/Button/VButton.vue"
import { getAllWashesAndWashSensorsCombined } from "../services/Chemical"

export default {
  name: "PackageConfiguratorPage",
  components: {
    PageTitle,
    LoadingSkeleton,
    VButton,
  },
  data() {
    return {
      loading: true,
      showLoadError: false,
      washes: [],
      maxVisibleChips: 6,
    }
  },
  computed: {
    ...mapState([
      "headers",
      "refreshed",
      "active_site"
    ]),
    ...mapGetters([
      "getSiteId",
      "modalOpen"
    ]),
    hasDataToDisplay() {
      return this.washes.length !== 0
    },
    totalContainerMappings() {
      return this.washes.reduce((sum, w) => sum + (w.containers?.length ?? 0), 0)
    },
    unconfiguredCount() {
      return this.washes.filter(w => !w.containers || w.containers.length === 0).length
    },
    URLparams() {
      return this.$route.query
    },
  },
  watch: {
    async refreshed(val) {
      if (val === true) {
        await this.loadData()
        this.refreshData(false)
      }
    },
    modalOpen(val, oldVal) {
      if (val !== oldVal && !val) {
        this.$router.push({ query: {} })
      }
    },
    async URLparams(val, oldVal) {

      const cameFromPop =
        this.$store.state.lastPopNavigationPosition ===
        window.history.state?.position;

      // Avoid duplicate calls if params were not changed through arrows
      if (!cameFromPop) {
        return;
      }

      if (val !== oldVal) {
        if (!val.id && this.modalOpen) {
          this.closeModal()
          this.$router.push({ query: {} })
        }
        else if (!this.modalOpen) this.openEditModalFunction(this.washes.find(x => x.wash_number == this.$route.query.id))
      }
    },
  },
  async mounted() {
    await this.loadData()
    if (this.$route.query?.id) {
      this.openEditModalFunction(this.washes.find(x => x.wash_number == this.$route.query.id))
    }
  },
  methods: {
    ...mapMutations([
      "setActiveModal",
      "closeModal",
      "refreshData",
      "setPrompt"
    ]),
    visibleContainers(wash) {
      return wash.containers ? wash.containers.slice(0, this.maxVisibleChips) : []
    },
    async loadData() {
      this.loading = true
      this.showLoadError = false
      await getAllWashesAndWashSensorsCombined(this.getSiteId, this.headers)
      .then(response => {
        if (response.status === 200 && response.data.results) {
          this.washes = response.data.results
        }
        else {
          console.log(response)
          this.showLoadError = true
          if (response.status === 403) {
            this.setPrompt(["You do not have the required permissions to access this data.", "closePrompt", null, { header: "Error" }])
          }
        }
      }).catch(error => {
        console.log(error)
        this.showLoadError = true
      })
      this.loading = false
    },
    async openEditModalFunction(wash) {
      if (wash === null || wash === undefined) return
      this.setActiveModal({
        component: "EditWashPkgChemicalsModal",
        args: { wash }
      })
      if (!this.$route.query.id) this.$router.push({
        query: { id: wash?.wash_number }
      })
    },
  }
}
</script>